import { jsPDF } from 'jspdf';

const GOLD   = [240, 180, 41];
const DARK   = [13, 43, 26];
const GREY   = [100, 116, 139];
const WHITE  = [255, 255, 255];
const LIGHT  = [248, 250, 252];
const BLACK  = [15, 23, 42];

function drawHeader(doc, champName, subtitle, pageWidth) {
  // Fond header
  doc.setFillColor(...DARK);
  doc.rect(0, 0, pageWidth, 42, 'F');

  // Ligne dorée bas header
  doc.setFillColor(...GOLD);
  doc.rect(0, 42, pageWidth, 1.5, 'F');

  // Logo texte
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(15);
  doc.setTextColor(...GOLD);
  doc.text('QUIZFOOT LEAGUE', pageWidth / 2, 16, { align: 'center' });

  // Nom du championnat
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(...WHITE);
  doc.text(champName.toUpperCase(), pageWidth / 2, 27, { align: 'center' });

  // Sous-titre
  if (subtitle) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(200, 200, 200);
    doc.text(subtitle, pageWidth / 2, 36, { align: 'center' });
  }
}

function drawFooter(doc, pageWidth, pageHeight) {
  doc.setFillColor(...DARK);
  doc.rect(0, pageHeight - 12, pageWidth, 12, 'F');
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7);
  doc.setTextColor(...GREY);
  doc.text('QuizFoot League — DEKANI SOFTWARE', pageWidth / 2, pageHeight - 4, { align: 'center' });
}

function drawGroupTable(doc, groupNum, players, startY, pageWidth) {
  const marginX = 14;
  const tableWidth = pageWidth - marginX * 2;
  let y = startY;

  // En-tête du groupe
  doc.setFillColor(...GOLD);
  doc.roundedRect(marginX, y, tableWidth, 8, 2, 2, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...DARK);
  doc.text(`GROUPE ${groupNum}`, marginX + 4, y + 5.5);
  y += 10;

  // En-tête colonnes
  doc.setFillColor(230, 235, 240);
  doc.rect(marginX, y, tableWidth, 7, 'F');
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7.5);
  doc.setTextColor(...GREY);
  doc.text('#',                  marginX + 3,    y + 5);
  doc.text('JOUEUR',             marginX + 12,   y + 5);
  doc.text('NIV.',               marginX + tableWidth - 28, y + 5);
  doc.text('SCORE',              marginX + tableWidth - 10, y + 5, { align: 'right' });
  y += 7;

  const sorted = [...players].sort((a, b) => (b.score ?? -1) - (a.score ?? -1));

  sorted.forEach((p, i) => {
    const rowH = 8;
    // Alternance couleur lignes
    doc.setFillColor(...(i % 2 === 0 ? WHITE : LIGHT));
    doc.rect(marginX, y, tableWidth, rowH, 'F');

    // Rang
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...(i < 2 ? GOLD : GREY));
    doc.text(String(i + 1), marginX + 3, y + 5.5);

    // Pseudo
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...BLACK);
    const pseudo = p.pseudo.length > 22 ? p.pseudo.slice(0, 21) + '…' : p.pseudo;
    doc.text(pseudo, marginX + 12, y + 5.5);

    // Niveau
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7.5);
    doc.setTextColor(...GREY);
    doc.text(String(p.level ?? '—'), marginX + tableWidth - 28, y + 5.5);

    // Score
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8);
    doc.setTextColor(...BLACK);
    const score = p.score !== null && p.score !== undefined ? String(p.score) : '—';
    doc.text(score, marginX + tableWidth - 10, y + 5.5, { align: 'right' });

    // Marqueur qualifié (top 2 avec score non nul)
    if (i < 2 && p.score !== null && p.score !== undefined) {
      doc.setFillColor(34, 197, 94);
      doc.circle(marginX + tableWidth - 2, y + 4, 1.5, 'F');
    }

    y += rowH;
  });

  // Bordure table
  doc.setDrawColor(220, 225, 230);
  doc.setLineWidth(0.3);
  doc.rect(marginX, startY, tableWidth, y - startY);

  return y + 6; // retourne la prochaine position Y
}

// ── Export toutes les poules ───────────────────────────────────────────────────
export function exportAllGroupsPDF(groupMap, champName) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth  = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const subtitle = `Tirage au sort · ${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}`;
  drawHeader(doc, champName, subtitle, pageWidth);
  drawFooter(doc, pageWidth, pageHeight);

  let y = 52;

  const groups = Object.entries(groupMap)
    .filter(([g]) => g !== '0')
    .sort(([a], [b]) => parseInt(a) - parseInt(b));

  groups.forEach(([g, players]) => {
    const estimatedHeight = 10 + 7 + players.length * 8 + 6;
    if (y + estimatedHeight > pageHeight - 20) {
      doc.addPage();
      drawHeader(doc, champName, subtitle, pageWidth);
      drawFooter(doc, pageWidth, pageHeight);
      y = 52;
    }
    y = drawGroupTable(doc, g, players, y, pageWidth);
  });

  const safeName = champName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  doc.save(`${safeName}_poules.pdf`);
}

// ── Export une seule poule ─────────────────────────────────────────────────────
export function exportSingleGroupPDF(groupNum, players, champName) {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const pageWidth  = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();

  const subtitle = `Groupe ${groupNum} · ${new Date().toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' })}`;
  drawHeader(doc, champName, subtitle, pageWidth);
  drawFooter(doc, pageWidth, pageHeight);

  drawGroupTable(doc, groupNum, players, 52, pageWidth);

  const safeName = champName.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  doc.save(`${safeName}_groupe_${groupNum}.pdf`);
}
